/**
 * Google Analytics 4 utilities
 */

declare global {
    interface Window {
        gtag?: (command: string, ...args: unknown[]) => void;
        dataLayer: unknown[];
    }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';

/**
 * Check if analytics cookies are allowed
 */
const hasAnalyticsConsent = (): boolean => {
    try {
        const consent = localStorage.getItem('cookie-consent');
        if (!consent) return false;
        const preferences = JSON.parse(consent);
        return preferences.analytics === true;
    } catch {
        return false;
    }
};

/**
 * Delete all analytics cookies
 */
const deleteAnalyticsCookies = () => {
    const cookiesToDelete = ['_ga', '_gid', '_gat', '_gat_gtag_' + GA_MEASUREMENT_ID];

    cookiesToDelete.forEach((cookieName) => {
        // Delete for current domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

        // Delete for both openleis.com domains
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.openleis.com`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=openleis.com`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=prod-cf.openleis.com`;
    });

    // Delete cookies that start with _ga_
    document.cookie.split(';').forEach((cookie) => {
        const cookieName = cookie.split('=')[0].trim();
        if (cookieName.startsWith('_ga_')) {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

            // Delete for both openleis.com domains
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.openleis.com`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=openleis.com`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=prod-cf.openleis.com`;
        }
    });
};

/**
 * Delete all marketing cookies
 */
const deleteMarketingCookies = () => {
    // Add marketing cookie names here when you implement marketing tools
    const marketingCookies = ['_fbp', '_fbc', 'fr']; // Example Facebook cookies

    marketingCookies.forEach((cookieName) => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

        // Delete for both openleis.com domains
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.openleis.com`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=openleis.com`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=prod-cf.openleis.com`;
    });
};

/**
 * Initialize Google Analytics 4 with consent management
 */
export const initGA = () => {
    if (typeof window === 'undefined') {
        return;
    }

    // Check if user has consented to analytics cookies
    if (!hasAnalyticsConsent()) {
        // If no consent, remove any existing analytics cookies and scripts
        deleteAnalyticsCookies();
        removeGA4Script();
        return;
    }

    // User has consented - load GA4 if not already loaded
    if (!document.getElementById('ga-script')) {
        loadGA4Direct();
    }
};

/**
 * Load GA4 gtag.js with cross-domain support
 */
const loadGA4Direct = () => {
    if (
        !GA_MEASUREMENT_ID ||
        GA_MEASUREMENT_ID === 'G-XXXXXXXXXX' ||
        document.getElementById('ga-script')
    ) {
        return;
    }

    // Create GA4 script element
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    gtagScript.id = 'ga-script';
    document.head.appendChild(gtagScript);

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };

    // Configure GA4 with cross-domain settings for prod-cf.openleis.com
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href,
        anonymize_ip: true, // GDPR compliance
        cookie_flags: 'max-age=7200;secure;samesite=none', // Cross-domain compatible
        cookie_domain: '.openleis.com', // Set cookies for parent domain
        linker: {
            domains: ['openleis.com', 'prod-cf.openleis.com'] // Enable cross-domain tracking
        }
    });
};

/**
 * Remove GA4 script
 */
const removeGA4Script = () => {
    const gaScript = document.getElementById('ga-script');
    if (gaScript) {
        gaScript.remove();
    }

    // Reset gtag function
    if (window.gtag) {
        window.gtag = undefined;
    }
};

/**
 * Handle consent changes - call this when user updates cookie preferences
 */
export const handleConsentChange = (preferences: { analytics: boolean; marketing: boolean }) => {
    // Handle analytics consent
    if (!preferences.analytics) {
        deleteAnalyticsCookies();
        removeGA4Script();
    } else {
        initGA();
    }

    // Handle marketing consent
    if (!preferences.marketing) {
        deleteMarketingCookies();
    }
};

/**
 * Track page views
 */
export const trackPageView = (url: string, title?: string) => {
    if (typeof window === 'undefined' || !window.gtag || !hasAnalyticsConsent()) {
        return;
    }

    window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: url,
        page_title: title || document.title
    });
};

/**
 * Track custom events
 */
export const trackEvent = (eventName: string, parameters?: Record<string, unknown>) => {
    if (typeof window === 'undefined' || !window.gtag || !hasAnalyticsConsent()) {
        return;
    }

    window.gtag('event', eventName, parameters);
};

/**
 * Track search events
 */
export const trackSearch = (searchTerm: string, resultCount?: number) => {
    trackEvent('search', {
        search_term: searchTerm,
        ...(resultCount !== undefined && { result_count: resultCount })
    });
};

/**
 * Track LEI record views
 */
export const trackLEIView = (lei: string, entityName?: string) => {
    trackEvent('lei_record_view', {
        lei_code: lei,
        ...(entityName && { entity_name: entityName })
    });
};

/**
 * Track export events
 */
export const trackExport = (format: 'csv' | 'json', recordCount?: number) => {
    trackEvent('export_data', {
        export_format: format,
        ...(recordCount !== undefined && { record_count: recordCount })
    });
};
